<template>
  <div class="mcontainer">
    <div class="job-admin">
      <div class="header-section">
        <div class="header-content">
          <div class="header-icon-wrap">
            <Icon icon="bx:bxs-briefcase" width="28" height="28" />
          </div>
          <div>
            <h1 class="page-title" style="color: white;">Kelola Job Opportunity</h1>
            <p class="page-subtitle">Kelola dan tambah lowongan pekerjaan</p>
          </div>
        </div>
        <div class="header-actions">
          <div class="search-box">
            <Icon icon="mdi:magnify" width="20" height="20" class="search-icon" />
            <input
              class="search-input"
              v-model="search"
              @input="debouncedFetch"
              placeholder="Cari job..."
            />
          </div>
          <select class="filter-select" v-model="jobType" @change="applyFilter">
            <option value="">Semua Tipe</option>
            <option value="Full Time">Full Time</option>
            <option value="Part Time">Part Time</option>
            <option value="Contract">Contract</option>
            <option value="Internship">Internship</option>
            <option value="Freelance">Freelance</option>
          </select>
          <router-link to="/app/admin/jobs/categories" class="btn-create btn-outline">
            <Icon icon="mdi:tag-multiple" width="20" height="20" />
            <span>Kategori</span>
          </router-link>
          <router-link to="/app/admin/jobs/create" class="btn-create">
            <Icon icon="mdi:plus" width="20" height="20" />
            <span>Tambah Job</span>
          </router-link>
        </div>
      </div>

      <div class="card-panel">
        <div class="table-wrapper">
          <table class="data-table">
            <thead>
              <tr>
                <th class="col-thumb">Gambar</th>
                <th class="col-title">Judul</th>
                <th class="col-type">Tipe</th>
                <th class="col-category">Kategori</th>
                <th class="col-salary">Gaji</th>
                <th class="col-actions">Aksi</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="it in list" :key="it.id" class="data-row">
                <td>
                  <div class="thumb-wrap">
                    <img v-if="it.images_url" :src="it.images_url" class="thumb" alt="">
                    <div v-else class="thumb placeholder">
                      <Icon icon="bx:bxs-briefcase" width="24" height="24" />
                    </div>
                  </div>
                </td>
                <td>
                  <div class="job-info">
                    <div class="job-title">{{ it.title }}</div>
                    <div class="job-desc">{{ truncate(it.description, 80) }}</div>
                  </div>
                </td>
                <td>
                  <span class="type-badge">{{ it.job_type }}</span>
                </td>
                <td>
                  <span v-if="it.category" class="text-sm">{{ it.category.title }}</span>
                  <span v-else-if="it.category_other" class="text-sm">{{ it.category_other }}</span>
                  <span v-else class="text-gray-400">—</span>
                </td>
                <td>
                  <span class="salary-text">Rp {{ formatNumber(it.min_salary) }} - Rp {{ formatNumber(it.max_salary) }}</span>
                </td>
                <td>
                  <div class="row-actions">
                    <router-link :to="{ name: 'JobAdminEdit', params: { id: it.id } }" class="action-btn action-edit" title="Edit">
                      <Icon icon="mdi:pencil" width="16" height="16" />
                      <span>Edit</span>
                    </router-link>
                    <button
                      class="action-btn action-delete"
                      @click="confirmDelete(it)"
                      :disabled="loading"
                      title="Hapus"
                    >
                      <Icon icon="mdi:trash-outline" width="16" height="16" />
                      <span>Hapus</span>
                    </button>
                  </div>
                </td>
              </tr>

              <tr v-if="!loading && list.length === 0">
                <td colspan="6">
                  <div class="empty-state">
                    <Icon icon="bx:bxs-briefcase" width="48" height="48" />
                    <p>Tidak ada job opportunity</p>
                    <router-link to="/app/admin/jobs/create" class="empty-link">Tambah job pertama</router-link>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="loading-overlay" v-if="loading">
          <div class="loading-spinner">
            <b-spinner />
          </div>
        </div>

        <div v-if="totalPage > 1" class="pagination-wrap">
          <span class="pagination-info">Halaman {{ page }} dari {{ totalPage }}</span>
          <div class="pagination-controls">
            <button class="icon-btn" :disabled="page===1 || loading" @click="go(1)" title="Pertama">
              <Icon icon="mdi:chevron-double-left" width="18" height="18" />
            </button>
            <button class="icon-btn" :disabled="page===1 || loading" @click="go(page-1)" title="Sebelumnya">
              <Icon icon="mdi:chevron-left" width="18" height="18" />
            </button>
            <button
              v-for="p in pages"
              :key="p"
              class="num-btn"
              :class="{ active: p === page }"
              :disabled="p === page || loading"
              @click="go(p)"
            >
              {{ p }}
            </button>
            <button class="icon-btn" :disabled="page===totalPage || loading" @click="go(page+1)" title="Selanjutnya">
              <Icon icon="mdi:chevron-right" width="18" height="18" />
            </button>
            <button class="icon-btn" :disabled="page===totalPage || loading" @click="go(totalPage)" title="Terakhir">
              <Icon icon="mdi:chevron-double-right" width="18" height="18" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { Icon } from '@iconify/vue2'

export default {
  name: 'JobAdmin',
  components: { Icon },
  data () {
    return {
      list: [],
      loading: false,
      page: 1,
      totalPage: 1,
      perPage: 10,
      jobType: '',
      search: '',
      debouncedFetch: null
    }
  },
  created () {
    this.debouncedFetch = this.debounce(this.fetchList, 500)
    this.fetchList()
  },
  computed: {
    pages () {
      const t = this.totalPage
      const cur = this.page
      const arr = []
      const win = 5
      let s = Math.max(1, cur - 2)
      let e = Math.min(t, s + win - 1)
      if (e - s < win - 1) s = Math.max(1, e - win + 1)
      for (let i = s; i <= e; i++) arr.push(i)
      return arr
    }
  },
  methods: {
    stripHtml (s) {
      if (!s) return ''
      return s.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
    },
    truncate (s, n) {
      const plain = this.stripHtml(s)
      return plain.length > n ? plain.slice(0, n) + '...' : plain
    },
    formatNumber (n) {
      if (n == null) return '0'
      return Number(n).toLocaleString('id-ID')
    },
    debounce (fn, d) {
      let t = null
      return (...a) => {
        clearTimeout(t)
        t = setTimeout(() => fn.apply(this, a), d)
      }
    },
    async fetchList () {
      this.loading = true
      try {
        const params = new URLSearchParams()
        params.set('page', this.page)
        params.set('per_page', this.perPage)
        if (this.jobType) params.set('job_type', this.jobType)
        if (this.search) params.set('search', this.search)
        const { data } = await axios.get(`/admin/jobs?${params.toString()}`)
        this.list = (data && data.data) ? data.data : []
        this.page = (data && data.current_page) ? data.current_page : 1
        this.totalPage = (data && data.last_page) ? data.last_page : 1
      } catch (e) {
        this.$swal({ icon: 'error', title: 'Error', text: e.response?.data?.message || 'Gagal memuat data' })
      } finally {
        this.loading = false
      }
    },
    go (p) {
      if (p < 1 || p > this.totalPage || p === this.page) return
      this.page = p
      this.fetchList()
    },
    applyFilter () {
      this.page = 1
      this.fetchList()
    },
    confirmDelete (item) {
      this.$swal({
        title: 'Yakin hapus?',
        text: `Job "${item.title}" akan dihapus`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ya, hapus',
        cancelButtonText: 'Batal'
      }).then((result) => {
        if (result.isConfirmed) {
          this.destroy(item.id)
        }
      })
    },
    async destroy (id) {
      try {
        await axios.delete(`/admin/jobs/${id}`)
        this.$swal({ icon: 'success', title: 'Berhasil', text: 'Job berhasil dihapus' })
        this.fetchList()
      } catch (e) {
        this.$swal({ icon: 'error', title: 'Error', text: e.response?.data?.message || 'Gagal menghapus' })
      }
    }
  }
}
</script>

<style scoped>
.job-admin {
  padding: 0;
  max-width: 100%;
}

.header-section {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 24px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  border-radius: 16px;
  color: #fff;
  box-shadow: 0 4px 20px rgba(14, 165, 233, 0.35);
}

.header-content {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.header-icon-wrap {
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 14px;
  backdrop-filter: blur(8px);
}

.page-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.page-subtitle {
  margin: 4px 0 0;
  font-size: 14px;
  opacity: 0.9;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.search-box {
  position: relative;
  min-width: 260px;
}

.search-icon {
  position: absolute;
  top: 50%;
  left: 14px;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.7);
  pointer-events: none;
}

.search-input {
  width: 100%;
  height: 42px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  padding: 0 16px 0 44px;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  font-size: 14px;
  transition: all 0.2s ease;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.7);
}

.filter-select {
  height: 42px;
  padding: 0 14px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  font-size: 14px;
  cursor: pointer;
}

.btn-create {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 42px;
  padding: 0 20px;
  background: #fff;
  color: #0ea5e9;
  font-weight: 600;
  font-size: 14px;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.2s ease;
}

.btn-create:hover {
  background: #f8fafc;
  transform: translateY(-1px);
}

.btn-create.btn-outline {
  background: transparent;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.5);
}

.btn-create.btn-outline:hover {
  background: rgba(255, 255, 255, 0.15);
}

.card-panel {
  position: relative;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.table-wrapper {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table thead th {
  background: #f8fafc;
  padding: 14px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 2px solid #e2e8f0;
}

.col-thumb { width: 100px; }
.col-title { min-width: 200px; }
.col-type { width: 120px; }
.col-category { width: 140px; }
.col-salary { min-width: 180px; }
.col-actions { width: 200px; }

.data-row td {
  padding: 16px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
  font-size: 14px;
}

.data-row:hover td {
  background: #f8fafc;
}

.thumb {
  width: 80px;
  height: 56px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}

.thumb.placeholder {
  width: 80px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  color: #94a3b8;
  border-radius: 10px;
  border: 1px dashed #cbd5e1;
}

.job-info {
  max-width: 280px;
}

.job-title {
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 2px;
}

.job-desc {
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
}

.type-badge {
  display: inline-flex;
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
  background: #e0f2fe;
  color: #0369a1;
  border: 1px solid #7dd3fc;
}

.salary-text {
  font-size: 13px;
  color: #475569;
}

.row-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.15s ease;
}

.action-edit {
  background: #dbeafe;
  color: #1d4ed8;
}

.action-edit:hover {
  background: #bfdbfe;
}

.action-delete {
  background: #fee2e2;
  color: #b91c1c;
}

.action-delete:hover {
  background: #fecaca;
}

.action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.empty-state {
  padding: 48px 24px;
  text-align: center;
  color: #94a3b8;
}

.empty-state .iconify {
  margin-bottom: 12px;
  opacity: 0.5;
}

.empty-state p {
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 500;
}

.empty-link {
  color: #0ea5e9;
  font-weight: 600;
  text-decoration: none;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.loading-spinner {
  color: #0ea5e9;
}

.pagination-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  flex-wrap: wrap;
  gap: 12px;
}

.pagination-info {
  font-size: 13px;
  color: #64748b;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 6px;
}

.icon-btn, .num-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 10px;
  font-size: 13px;
  cursor: pointer;
  color: #475569;
  transition: all 0.15s ease;
}

.icon-btn:hover:not(:disabled), .num-btn:hover:not(:disabled):not(.active) {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.num-btn.active {
  background: #0ea5e9;
  border-color: #0ea5e9;
  color: #fff;
}

.icon-btn:disabled, .num-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
